<script setup lang="ts">
import type ListingEmptyState from '../ListingEmptyState/EmptyState.vue';
import type { ButtonVariants } from '../ui/button';
import type { OrdenationDropdownOption } from '~/components/Filters/OrdenationSelect';
import type { RegionInfoLinkClickedEmitProps } from '~/components/RegionInfo';
import type {
	BreadcrumbItem,
	BreadcrumbItemWithIndex
} from '~/components/ui/breadcrumb';
import type { PropertyTypeEnum } from '~/core/entities/CRMEnums';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { SectionName } from '~/types/mixpanel';
import type { Region } from '~~/core/entities/Region';
import type { ClassValue } from 'clsx';
import type { FunctionalComponent, SVGAttributes } from 'vue';

import { cn } from '@/utils';
import { useBreakpoints } from '@vueuse/core';
import { NavigationSectionNavigation } from '#components';
import { useEvents } from '~/composables/events/useEvents';
import { useAdaptivePadding } from '~/composables/useAdaptivePadding';
import { useRegionInfos } from '~/composables/useRegionInfos';
import { MixpanelProps } from '~/core/service/mixpanel';

import { Container } from '~/components/Container';
import { OrdenationSelect } from '~/components/Filters/OrdenationSelect';

import PropertyListPagination from '../PropertyListPagination';
import PropertiesLinkingByRoom from '../RegionPropertiesLinking/PropertiesLinkingByRoom.vue';
import { CondosNeighborhoods } from '../Sections/CondosNeighborhoods';
import { SurroundingNeighborhoods } from '../Sections/SurroundingNeighborhoods';

interface Props {
	isUserDragging: boolean;
	isFetchingForTheFirstTime: boolean;
	hasProperties: boolean;
	propertyCountLabel: string;
	infoLabel: string | null;
	filters: PropertyFilters;
	isFetching: boolean;
	properties: any[];
	shouldShowNextPageButton: boolean;
	emptyStateData: {
		title: string;
		description: string;
		icon?: FunctionalComponent<SVGAttributes, {}, any>;
	} | null;
	breadcrumbItems: BreadcrumbItem[];
	isFixedBottom: boolean;
	menuBottomLevel: number;
	currentY: number;
	isDragging: boolean;
	isTransitioning: boolean;
	showCardActions?: boolean;
	section?: SectionName;
	ordenationOptions?: OrdenationDropdownOption[];
	defaultSortBy?: OrdenationDropdownOption['sortBy'];
	defaultOrderBy?: OrdenationDropdownOption['orderBy'];
	countLabelClasses?: string;
	containerClasses?: string;
	ordenationSelectButtonClasses?: ClassValue;
	propertyCountLabelClasses?: ClassValue;
	ordenationSelectSize?: ButtonVariants['size'];
	ordenationSelectClasses?: string;
}

const props = defineProps<Props>();

const headerDragOffsetY = useState<number>('header-drag-offset-y', () => 0);

function getSkeletonClasses(
	shouldShowMobile: boolean,
	shouldShowDesktop: boolean
) {
	if (shouldShowMobile && shouldShowDesktop) return 'block';
	if (shouldShowMobile) return 'block md:hidden';
	if (shouldShowDesktop) return 'hidden md:block';
	return 'hidden';
}

// Always generate 4 skeletons, CSS controls visibility
const skeletonItems = computed(() => {
	const currentCount = props.properties.length;
	const maxSkeletons = 4;

	return Array.from({ length: maxSkeletons }, (_, index) => {
		const shouldShowMobile = index < Math.max(0, 2 - currentCount);
		const shouldShowDesktop = index < Math.max(0, maxSkeletons - currentCount);

		return {
			id: index,
			shouldShowMobile,
			shouldShowDesktop,
			classes: getSkeletonClasses(shouldShowMobile, shouldShowDesktop)
		};
	});
});

// Initial skeleton items for first load (responsive)
const initialSkeletonItems = computed(() => {
	const maxSkeletons = 4;
	return Array.from({ length: maxSkeletons }, (_, index) => {
		const shouldShowMobile = index < 2;
		const shouldShowDesktop = index < maxSkeletons;

		return {
			id: index,
			shouldShowMobile,
			shouldShowDesktop,
			classes: getSkeletonClasses(shouldShowMobile, shouldShowDesktop)
		};
	});
});

const propertyListSectionRef = ref<HTMLElement | null>(null);
const footerRef = ref<HTMLElement | null>(null);
const viewMoreButtonRef = ref<HTMLElement | null>(null);
const emptyStateRef =
	useTemplateRef<InstanceType<typeof ListingEmptyState>>('emptyStateRef');

const emit = defineEmits<{
	(
		e: 'updateOrdenation',
		event: {
			sortBy: PropertyFilters['sortBy'];
			orderBy: PropertyFilters['orderBy'];
		}
	): void;
	(e: 'reorderClicked', value: OrdenationDropdownOption['value']): void;
	(e: 'nextPage'): void;
	(e: 'breadcrumbClicked', breadcrumbItem: BreadcrumbItemWithIndex): void;
	(e: 'regionClicked', props: RegionInfoLinkClickedEmitProps): void;
	(e: 'filterLinkClicked', filters: PropertyFilters): void;
	(e: 'footerVisibilityChanged', isVisible: boolean): void;
	(e: 'viewMoreButtonVisibilityChanged', isVisible: boolean): void;
	(e: 'viewMoreButtonPositionChanged', position: { top: number }): void;
}>();

const route = useRoute();

const { trackCtaViewMoreClicked } = useEvents();
const clickCount = ref(0);

const footerIsVisible = ref(false);
// Footer visibility tracking
useIntersectionObserver(
	footerRef,
	([entry]) => {
		if (!entry) return;
		const isIntersecting = entry.isIntersecting;
		footerIsVisible.value = isIntersecting;
		emit('footerVisibilityChanged', isIntersecting);
	},
	{ threshold: 0.1 }
);

// View more button visibility and position tracking
useIntersectionObserver(
	viewMoreButtonRef,
	([entry]) => {
		if (!entry) return;
		const isIntersecting = entry.isIntersecting;
		emit('viewMoreButtonVisibilityChanged', isIntersecting);

		// Emit position whenever the button becomes visible or changes
		if (viewMoreButtonRef.value) {
			emit('viewMoreButtonPositionChanged', {
				top: viewMoreButtonRef.value.offsetTop
			});
		}
	},
	{ threshold: 0.1 }
);

// Watch for changes in button position even when not intersecting
const resizeObserver = ref<ResizeObserver | null>(null);

watch(viewMoreButtonRef, el => {
	// Clean up previous observer if it exists
	if (resizeObserver.value) {
		resizeObserver.value.disconnect();
		resizeObserver.value = null;
	}

	if (el) {
		const observer = new ResizeObserver(() => {
			emit('viewMoreButtonPositionChanged', {
				top: el.offsetTop
			});
		});
		observer.observe(el);
		resizeObserver.value = observer;
	}
});

// Clean up observer on component unmount
onUnmounted(() => {
	if (resizeObserver.value) {
		resizeObserver.value.disconnect();
	}
});

function handleUpdateOrdenation(event: {
	sortBy: PropertyFilters['sortBy'];
	orderBy: PropertyFilters['orderBy'];
}) {
	emit('updateOrdenation', event);
}

function handleReorderClicked(value: OrdenationDropdownOption['value']) {
	emit('reorderClicked', value);
}

async function scrollToTop() {
	const { y } = useScroll(propertyListSectionRef);
	if (y.value > 0) {
		propertyListSectionRef.value?.scrollTo({ top: 0 });

		await until(y).toBe(0);
	}
}

function handleNextPage() {
	clickCount.value++;
	trackCtaViewMoreClicked({
		[MixpanelProps.CURRENT_PAGE]: route.name?.toString() || '',
		[MixpanelProps.DESTINATION_URL]: route.fullPath,
		[MixpanelProps.CLICK_COUNT]: clickCount.value,
		[MixpanelProps.CTA_TEXT]: 'Ver mais'
	});
	emit('nextPage');
}

function handleBreadcrumbClicked(breadcrumbItem: BreadcrumbItemWithIndex) {
	emit('breadcrumbClicked', breadcrumbItem);
}

function handleRegionClicked(props: RegionInfoLinkClickedEmitProps) {
	emit('regionClicked', props);
}

const handleFilterLinkClicked = useDebounceFn((filters: PropertyFilters) => {
	emit('filterLinkClicked', filters);
}, 300);

const safeCity = computed(() => props.filters.cities?.[0] || '');
const safeState = computed(() => props.filters.states?.[0] || '');

const selectedRegion = computed(() => props.filters.regions?.[0] || '');
const selectedPropertyType = computed<PropertyTypeEnum>(
	() => props.filters.propertyTypes?.[0] || ('' as PropertyTypeEnum)
);

const { regionInfo, regionName, defaultAllowedFilters } = useRegionInfos(
	toRef(props, 'filters')
);

const hasOnlyOneRegion = computed<boolean>(
	() => (props.filters?.regions?.length || 0) === 1
);

const hasOnlyOnePropertyType = computed<boolean>(
	() => (props.filters?.propertyTypes?.length || 0) === 1
);

/**
 * Check if a filter value is non-empty/active
 */
function isFilterValueActive(value: unknown): boolean {
	if (Array.isArray(value)) {
		return value.length > 0;
	}

	if (typeof value === 'number') {
		return true;
	}

	if (typeof value === 'boolean') {
		return value;
	}

	if (typeof value === 'string') {
		return value.length > 0;
	}

	return false;
}

/**
 * Check if there are any disallowed filters active
 */
function checkForDisallowedFilters(
	allowedFiltersList: Array<keyof typeof props.filters | string>
): boolean {
	return Object.keys(props.filters || {}).some(key => {
		if (allowedFiltersList.includes(key as keyof typeof props.filters)) {
			return false;
		}

		const value = props.filters?.[key as keyof typeof props.filters];
		return isFilterValueActive(value);
	});
}

const hasPropertiesLinkingByRoom = computed<boolean>(() => {
	const hasDisallowedFilters = checkForDisallowedFilters(defaultAllowedFilters);

	return !hasDisallowedFilters && hasOnlyOneRegion.value;
});

const hasRegionAndPropertyTypes = computed<boolean>(() => {
	const hasDisallowedFilters = checkForDisallowedFilters([
		...defaultAllowedFilters,
		'propertyTypes'
	]);

	return (
		!hasDisallowedFilters &&
		hasOnlyOneRegion.value &&
		hasOnlyOnePropertyType.value
	);
});

const hasRegionAndOrPropertyTypes = computed<boolean>(
	() => hasRegionAndPropertyTypes.value || hasPropertiesLinkingByRoom.value
);

const shouldShowRegionInfoSection = computed<boolean>(() => {
	const hasValidBreadcrumb = props.breadcrumbItems.length > 0;
	const hasValidLocation =
		!!selectedRegion.value && !!safeCity.value && !!safeState.value;
	const hasValidRegionInfo =
		regionInfo.value && hasPropertiesLinkingByRoom.value;
	const hasValidPropertyType =
		hasRegionAndPropertyTypes.value && !!selectedPropertyType.value;

	return (
		hasValidBreadcrumb &&
		hasValidLocation &&
		(hasValidRegionInfo || hasValidPropertyType)
	);
});

const breadCrumbClasses = computed(() =>
	cn('bg-white-dark-1 !pt-8 !px-0', {
		'!pb-0':
			!hasRegionAndOrPropertyTypes.value || !shouldShowRegionInfoSection.value
	})
);

const shouldShowBreadcrumbOnly = computed(() => {
	const regionKeys: Array<keyof Region> = ['hero', 'seo', 'questions'];
	const hasValidRegionInfo =
		regionInfo.value && regionKeys.some(key => regionInfo.value?.[key]);

	return (
		props.breadcrumbItems.length &&
		!shouldShowRegionInfoSection.value &&
		!hasValidRegionInfo
	);
});

const hasLessThan10Properties = computed(() => {
	return props.properties.length < 10 && props.properties.length > 0;
});

const adaptivePaddingConfig = computed(() => {
	return {
		expandedPadding: 96,
		collapsedPadding: 0,
		dragBottomOffset: 220,
		fallbackViewportHeight: 800
	};
});

const { transitionClasses, dynamicStyle } = useAdaptivePadding({
	currentY: toRef(props, 'currentY'),
	isDragging: toRef(props, 'isDragging'),
	forceExpanded: hasLessThan10Properties,
	config: adaptivePaddingConfig
});

const breakpoints = useBreakpoints({
	md: 768
});

const isMobile = breakpoints.smallerOrEqual('md');

// Explicit hydration state to prevent dynamic styles during SSR/hydration
const isHydrated = ref(false);

onMounted(() => {
	isHydrated.value = true;
});

const emptyStateClasses = computed(() => {
	const baseClasses = 'py-24 2xl:py-[100px]';
	const staticTransitionClasses = 'transition-all duration-500 ease-out';
	return `${baseClasses} ${staticTransitionClasses}`;
});

const emptyStateDynamicStyle = computed(() => {
	// Only apply dynamic styles after component is fully hydrated
	if (!isHydrated.value) {
		return {};
	}

	return isMobile.value ? dynamicStyle.value : {};
});

const shouldShowMapContainerBottomPadding = computed(() => {
	return (
		props.menuBottomLevel <= 1 &&
		!props.emptyStateData &&
		props.properties.length !== 0
	);
});

defineExpose({
	scrollToTop,
	footerIsVisible,
	emptyStateRef,
	footerRef
});
</script>

<template>
	<section
		id="property-list-section"
		ref="propertyListSectionRef"
		as="section"
		:class="
			cn(
				'lg:overflow-auto!',
				$attrs.class as string,
				menuBottomLevel === 0 ? 'overflow-y-auto' : 'overflow-y-hidden'
			)
		"
	>
		<Container
			:class="
				cn('min-h-dvh pb-11! pt-0! lg:pb-[60px]! lg:pt-6!', containerClasses, {
					'pb-0! lg:pb-0!': hasRegionAndOrPropertyTypes
				})
			"
		>
			<div
				class="flex w-full items-end justify-between pb-5 transition-all duration-300 ease-out"
			>
				<div
					v-if="isFetchingForTheFirstTime"
					class="flex w-full items-center justify-between"
				>
					<div class="w-[70%] space-y-1">
						<Skeleton is-active variant="white" class="h-[28px] w-[100px]" />
						<Skeleton is-active variant="white" class="h-[21px] w-[200px]" />
					</div>
					<Skeleton is-active variant="white" class="h-[40px] w-[120px]" />
				</div>

				<div
					v-else-if="hasProperties"
					class="flex w-full items-center justify-between"
				>
					<div class="text-left">
						<h1
							:class="
								cn(
									'flex flex-col font-matter-sq text-xl font-normal leading-normal tracking-[-0.4px] text-primary',
									countLabelClasses
								)
							"
						>
							<span>
								{{ propertyCountLabel }}
							</span>
							<span
								:class="
									cn(
										'text-sm font-normal leading-normal text-primary',
										propertyCountLabelClasses
									)
								"
							>
								{{ infoLabel }}
							</span>
						</h1>
					</div>
					<div class="flex items-center gap-1 xl:gap-[0.63rem]">
						<slot name="actions" />
						<OrdenationSelect
							:sort-by="filters.sortBy"
							:order-by="filters.orderBy"
							:is-transitioning="isTransitioning"
							:options="ordenationOptions"
							:default-sort-by="defaultSortBy"
							:default-order-by="defaultOrderBy"
							:button-size="props.ordenationSelectSize"
							:button-classes="ordenationSelectButtonClasses"
							@update-selection="handleUpdateOrdenation"
							@reorder-clicked="handleReorderClicked"
						/>
					</div>
				</div>
			</div>

			<div
				class="grid w-full max-w-full grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2"
				:class="{
					'pb-5': shouldShowMapContainerBottomPadding
				}"
			>
				<ClientOnly>
					<template v-if="isFetchingForTheFirstTime">
						<PropertyCardSkeleton
							v-for="skeleton in initialSkeletonItems"
							:key="`initial-skeleton-${skeleton.id}`"
							variant="whiteBackground"
							:class="skeleton.classes"
						/>
					</template>
					<template v-else>
						<PropertyListPagination
							:properties="properties"
							:show-card-actions="showCardActions"
							:section="section"
						/>
					</template>

					<template v-if="isFetching">
						<div class="contents contain-[layout] [overflow-anchor:none]">
							<PropertyCardSkeleton
								v-for="skeleton in skeletonItems"
								:key="`pagination-skeleton-${skeleton.id}`"
								variant="whiteBackground"
								:class="
									cn(
										skeleton.classes,
										'contain-[style_layout] [content-visibility:auto] [overflow-anchor:none]'
									)
								"
							/>
						</div>
					</template>
					<template #fallback>
						<PropertyListPagination
							:properties="properties"
							:show-card-actions="showCardActions"
							:section="section"
						/>
					</template>
				</ClientOnly>
			</div>

			<div id="view-more-button" ref="viewMoreButtonRef">
				<Button
					v-if="shouldShowNextPageButton"
					class="mx-auto mt-5 w-[150px] cursor-pointer px-11 font-medium"
					@click="handleNextPage"
				>
					<span class="flex items-center justify-center">
						<IconPilarCircularProgress
							v-show="isFetching"
							class="h-[26px] animate-spin text-white"
						/>
						<span v-show="!isFetching">Ver mais</span>
					</span>
				</Button>
			</div>

			<div v-show="emptyStateData" class="w-full">
				<ListingEmptyState
					ref="emptyStateRef"
					:class="emptyStateClasses"
					:style="emptyStateDynamicStyle"
					title-classes="text-xl md:text-2xl "
					description-classes="text-sm md:text-lg"
					:show-badges="false"
				>
					<template v-if="emptyStateData?.icon" #icon>
						<component
							:is="emptyStateData.icon"
							:class="cn('size-7 text-white')"
						/>
					</template>
					<template #title>{{ emptyStateData?.title }}</template>
					<template #description>
						{{ emptyStateData?.description }}
					</template>
					<template #cta>
						<slot name="empty-state-cta" />
					</template>
				</ListingEmptyState>
			</div>
		</Container>

		<Container
			v-if="hasRegionAndOrPropertyTypes"
			class="px-0!"
			main-class="flex flex-col *:px-5 *:md:px-12"
		>
			<SurroundingNeighborhoods
				v-if="hasRegionAndOrPropertyTypes"
				:region="selectedRegion"
				:property-type="selectedPropertyType"
				class="border-t border-white-dark-2"
				@filter-link-clicked="handleFilterLinkClicked"
			/>

			<CondosNeighborhoods
				v-if="hasRegionAndOrPropertyTypes"
				:region="selectedRegion"
				:property-type="selectedPropertyType"
				class="border-t border-white-dark-2"
				container-class="pb-0!"
			/>
		</Container>

		<Container
			v-if="shouldShowRegionInfoSection || breadcrumbItems.length"
			:class="breadCrumbClasses"
			:main-class="
				cn('flex flex-col px-5 md:px-12', {
					'pb-6 md:pb-8': shouldShowBreadcrumbOnly
				})
			"
		>
			<PropertiesLinkingByRoom
				v-if="hasPropertiesLinkingByRoom"
				:region="selectedRegion"
				:city="safeCity"
				:state="safeState"
				:breadcrumb-items="breadcrumbItems"
				section-name="pre-footer"
				@breadcrumb-clicked="handleBreadcrumbClicked"
				@filter-link-clicked="handleFilterLinkClicked"
			/>

			<RegionPropertiesLinking
				v-else-if="hasRegionAndPropertyTypes && selectedPropertyType"
				:property-type="selectedPropertyType"
				:breadcrumb-items="breadcrumbItems"
				:region="selectedRegion"
				:city="safeCity"
				:state="safeState"
				section-name="pre-footer"
				@breadcrumb-clicked="handleBreadcrumbClicked"
				@filter-link-clicked="handleFilterLinkClicked"
			/>

			<Breadcrumb
				v-else-if="shouldShowBreadcrumbOnly"
				:items="breadcrumbItems"
				@breadcrumb-clicked="handleBreadcrumbClicked"
			/>
		</Container>
		<Container
			v-if="regionInfo?.hero"
			class="bg-white-dark-2 py-[28px]!"
			main-class="max-w-full!"
		>
			<RegionInfo
				:name="regionInfo?.hero?.title"
				:description-paragraphs="regionInfo?.seo?.discoveryContent?.paragraphs"
				class="px-0"
				@region-clicked="handleRegionClicked"
			/>
		</Container>

		<Container v-if="regionInfo?.hero" class="bg-white-dark-3 py-[28px]! px-5">
			<RegionQuestions
				:name="regionName"
				:questions="regionInfo?.questions"
				:title="`Dúvidas Frequentes sobre ${regionName}`"
				content-block-variant="white-dark-3"
				accordion-item-class="bg-white"
				accordion-trigger-as="h2"
				class="px-0"
			/>
		</Container>

		<Container class="bg-primary-light-4 py-0">
			<NavigationSectionNavigation links-container-wrapper-variant="column" />
		</Container>
		<Container ref="footerRef" class="bg-primary py-0!">
			<PortalFooter />
		</Container>
	</section>
</template>
